import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Table,
  Space,
  Input,
  Row,
  Col,
  Tooltip,
  Statistic,
  message,
  Modal,
  Select,
  DatePicker,
  ConfigProvider
} from 'antd';
import {
  FilterOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import locale from 'antd/locale/vi_VN';
import api from '../../services/api';
import styles from './StockInPage.module.css';
import { StockInPrintModal } from '../../components/stock-in';
const { Search } = Input;
const { Option } = Select;

interface StockInItem {
  id: number;
  medicine_id: number;
  medicine_name: string;
  medicine_code: string;
  quantity: number;
  unit: string;
  unit_price: number;
  lot_number: string;
  expiry_date: string;
  manufacturing_date: string;
  supplier_id: number;
  supplier_name: string;
  total_amount: number;
  notes: string;
}

interface StockInRecord {
  id: number;
  voucher_number: string;
  supplier_id: number;
  supplier_name: string;
  total_items: number;
  total_amount: number;
  status: 'pending' | 'completed' | 'cancelled';
  created_at: string;
  completed_at?: string;
  created_by_name: string;
  notes: string;
  items: StockInItem[];
}

interface Supplier {
  id: number;
  name: string;
  code: string;
  contact_person: string;
  phone: string;
}

const StockInPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    supplier: '',
    date_from: dayjs().startOf('month').format('YYYY-MM-DD'),
    date_to: dayjs().endOf('month').format('YYYY-MM-DD')
  } as {
    status: string;
    supplier: string;
    date_from: string | undefined;
    date_to: string | undefined;
  });
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [printingRecordId, setPrintingRecordId] = useState<number | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const queryClient = useQueryClient();

  // Fetch data
  const { data: stockInData = { data: [], total: 0 }, isLoading } = useQuery(
    ['stock-in', searchText, filters, pagination.current, pagination.pageSize],
    () => api.get('/api/stock/in', {
      params: {
        search: searchText,
        status: filters.status,
        supplier_id: filters.supplier,
        start_date: filters.date_from,
        end_date: filters.date_to,
        page: pagination.current,
        pageSize: pagination.pageSize
      }
    }).then(res => {
      // res.data: { data: [], total: number }
      setPagination(prev => ({ ...prev, total: res.data.total || 0 }));
      return res.data;
    }),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    }
  );
  const stockInRecords = stockInData.data;
  const totalRecords = stockInData.total;

  // Fetch detailed record for printing
  const { data: printDetailRecord, isLoading: isLoadingPrintDetail } = useQuery(
    ['stock-in-print-detail', printingRecordId],
    () => api.get(`/api/stock/in/${printingRecordId}`).then(res => res.data.data),
    {
      enabled: !!printingRecordId && printModalVisible,
      staleTime: 1 * 60 * 1000,
    }
  );

  const { data: suppliers = [] } = useQuery(
    'suppliers',
    () => api.get('/api/suppliers').then(res => res.data.data),
    {
      staleTime: 10 * 60 * 1000,
    }
  );

  // Mutations for other actions
  const completeMutation = useMutation(
    (id: number) => api.post(`/api/stock/in/${id}/complete`),
    {
      onSuccess: () => {
        message.success('Hoàn thành nhập kho thành công!');
        queryClient.invalidateQueries('stock-in');
        queryClient.invalidateQueries('inventory');
      },
      onError: (error: any) => {
        console.error('Complete mutation error:', error);
        console.error('Error response:', error.response?.data);
        const errorMessage = error.response?.data?.details || error.response?.data?.message || 'Có lỗi xảy ra khi hoàn thành nhập kho';
        message.error(errorMessage);
      },
    }
  );

  const deleteMutation = useMutation(
    (id: number) => api.delete(`/api/stock/in/${id}`),
    {
      onSuccess: () => {
        message.success('Xóa phiếu nhập kho thành công!');
        queryClient.invalidateQueries('stock-in');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi xóa phiếu nhập kho');
      },
    }
  );

  // Handlers
  const handleAdd = () => {
    navigate('/stock-in/new');
  };

  const handleEdit = (record: StockInRecord) => {
    navigate(`/stock-in/edit/${record.id}`);
  };

  const handlePrint = (record: StockInRecord) => {
    setPrintingRecordId(record.id);
    setPrintModalVisible(true);
  };

  const handleComplete = (id: number) => {
    console.log('Handling complete for ID:', id);
    Modal.confirm({
      title: 'Xác nhận hoàn thành nhập kho',
      content: 'Bạn có chắc chắn muốn hoàn thành phiếu nhập kho này? Hành động này sẽ cập nhật tồn kho.',
      okText: 'Hoàn thành',
      cancelText: 'Hủy',
      onOk: () => {
        console.log('Modal confirmed, calling mutation for ID:', id);
        completeMutation.mutate(id);
      },
    });
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Xác nhận xóa phiếu nhập kho',
      content: 'Bạn có chắc chắn muốn xóa phiếu nhập kho này? Hành động này không thể hoàn tác.',
      okText: 'Xóa',
      cancelText: 'Hủy',
      okType: 'danger',
      onOk: () => deleteMutation.mutate(id),
    });
  };

  const handlePrintModalCancel = () => {
    setPrintModalVisible(false);
    setPrintingRecordId(null);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const clearFilters = () => {
    setFilters({ 
      status: '', 
      supplier: '', 
      date_from: dayjs().startOf('month').format('YYYY-MM-DD'),
      date_to: dayjs().endOf('month').format('YYYY-MM-DD')
    });
    setSearchText('');
  };

  // Statistics
  const pendingRecords = stockInRecords.filter((r: StockInRecord) => r.status === 'pending').length;
  const completedRecords = stockInRecords.filter((r: StockInRecord) => r.status === 'completed').length;
  const totalAmount = stockInRecords.reduce((sum: number, r: StockInRecord) => sum + r.total_amount, 0);

  // Filtered data (nếu cần filter thêm phía client)
  const filteredRecords = stockInRecords; // Đã filter phía server

  const columns = [
    {
      title: 'Mã phiếu',
      dataIndex: 'voucher_number',
      key: 'voucher_number',
      width: 160,
      fixed: 'left' as const,
      render: (ref: string, record: StockInRecord) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ 
            fontWeight: '600', 
            fontSize: '15px',
            color: '#1f2937',
            lineHeight: '1.2'
          }}>
            {ref}
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: '#6b7280',
            fontWeight: '400'
          }}>
            {dayjs(record.created_at).format('DD/MM/YYYY HH:mm')}
          </div>
        </div>
      ),
    },
    {
      title: 'Nhà cung cấp',
      key: 'supplier',
      width: 200,
      render: (_: any, record: StockInRecord) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ 
            fontWeight: '500', 
            fontSize: '14px',
            color: '#1f2937',
            lineHeight: '1.3'
          }}>
            {record.supplier_name}
          </div>
          {suppliers.find((s: Supplier) => s.id === record.supplier_id)?.contact_person && (
            <div style={{ 
              fontSize: '12px', 
              color: '#6b7280',
              fontWeight: '400'
            }}>
              {suppliers.find((s: Supplier) => s.id === record.supplier_id)?.contact_person}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Thông tin',
      key: 'info',
      width: 140,
      render: (_: any, record: StockInRecord) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            fontSize: '13px',
            color: '#374151'
          }}>
            <span style={{ fontWeight: '500' }}>{record.total_items} món</span>
          </div>
          <div style={{ 
            fontSize: '14px',
            fontWeight: '600',
            color: '#059669'
          }}>
            {record.total_amount.toLocaleString('vi-VN')}đ
          </div>
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const statusConfig = {
          pending: { 
            color: '#f59e0b', 
            bgColor: '#fef3c7',
            text: 'Chờ xử lý',
            icon: 'fas fa-clock'
          },
          completed: { 
            color: '#059669', 
            bgColor: '#d1fae5',
            text: 'Hoàn thành',
            icon: 'fas fa-check-circle'
          },
          cancelled: { 
            color: '#dc2626', 
            bgColor: '#fee2e2',
            text: 'Đã hủy',
            icon: 'fas fa-times-circle'
          }
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            padding: '6px 12px',
            borderRadius: '20px',
            backgroundColor: config.bgColor,
            color: config.color,
            fontSize: '12px',
            fontWeight: '500',
            width: 'fit-content'
          }}>
            {config.text}
          </div>
        );
      },
      filters: [
        { text: 'Chờ xử lý', value: 'pending' },
        { text: 'Hoàn thành', value: 'completed' },
        { text: 'Đã hủy', value: 'cancelled' }
      ],
      onFilter: (value: any, record: StockInRecord) => record.status === value,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: StockInRecord) => (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <Tooltip title="In phiếu">
            <Button
              type="text"
              icon={<i className="fas fa-print" />}
              onClick={() => handlePrint(record)}
              size="small"
              style={{ 
                color: '#6b7280',
                border: 'none',
                boxShadow: 'none'
              }}
            />
          </Tooltip>
          {record.status === 'pending' && (
            <>
              <Tooltip title="Sửa">
                <Button
                  type="text"
                  icon={<i className="fas fa-edit" />}
                  onClick={() => handleEdit(record)}
                  size="small"
                  style={{ 
                    color: '#f59e0b',
                    border: 'none',
                    boxShadow: 'none'
                  }}
                />
              </Tooltip>
              <Tooltip title="Hoàn thành">
                <Button
                  type="text"
                  icon={<i className="fas fa-check-circle" />}
                  onClick={() => handleComplete(record.id)}
                  size="small"
                  style={{ 
                    color: '#059669',
                    border: 'none',
                    boxShadow: 'none'
                  }}
                />
              </Tooltip>
              <Tooltip title="Xóa">
                <Button
                  type="text"
                  icon={<i className="fas fa-trash" />}
                  onClick={() => handleDelete(record.id)}
                  size="small"
                  style={{ 
                    color: '#dc2626',
                    border: 'none',
                    boxShadow: 'none'
                  }}
                />
              </Tooltip>
            </>
          )}
        </div>
      ),
    },
  ];

  // Thiết lập locale cho dayjs
  dayjs.locale('vi');

  const getSummaryStats = () => {
    return (
      <div className={styles.statsContainer}>
        <Row gutter={[24, 16]} className={styles.statsRow}>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#3b82f6' }}>
                  <i className="fas fa-box" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Tổng phiếu nhập"
                    value={totalRecords}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#f59e0b' }}>
                  <i className="fas fa-clock" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Chờ xử lý"
                    value={pendingRecords}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#10b981' }}>
                  <i className="fas fa-check-circle" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Đã hoàn thành"
                    value={completedRecords}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#8b5cf6' }}>
                  <i className="fas fa-dollar-sign" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Tổng giá trị"
                    value={totalAmount}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                    formatter={(value) => value?.toLocaleString('vi-VN')}
                  />
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <ConfigProvider locale={locale}>
      <div className={styles.container}>
        {getSummaryStats()}

        <Card className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <div className={styles.searchSection}>
              <Search
                placeholder="Tìm kiếm theo mã phiếu, tên nhà cung cấp (không phân biệt hoa thường)..."
                allowClear
                className={styles.searchInput}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={handleSearch}
                size="large"
                style={{ textAlign: 'left' }}
              />
            </div>
            <div className={styles.filterSection}>
              <Space size="middle" wrap>
                <DatePicker
                  placeholder="Từ ngày"
                  value={filters.date_from ? dayjs(filters.date_from) : undefined}
                  onChange={(date) => setFilters(prev => ({ ...prev, date_from: date ? dayjs(date).format('YYYY-MM-DD') : undefined }))}
                  style={{ minWidth: 150 }}
                />
                <DatePicker
                  placeholder="Đến ngày"
                  value={filters.date_to ? dayjs(filters.date_to) : undefined}
                  onChange={(date) => setFilters(prev => ({ ...prev, date_to: date ? dayjs(date).format('YYYY-MM-DD') : undefined }))}
                  style={{ minWidth: 150 }}
                />
                <Select
                  placeholder="Lọc theo trạng thái"
                  allowClear
                  value={filters.status}
                  onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                  style={{ minWidth: 150 }}
                >
                  <Option value="pending">Chờ xử lý</Option>
                  <Option value="completed">Hoàn thành</Option>
                  <Option value="cancelled">Đã hủy</Option>
                </Select>
                {(filters.status || filters.date_from || filters.date_to || searchText) && (
                  <Button
                    type="link"
                    icon={<FilterOutlined />}
                    onClick={clearFilters}
                    size="small"
                  >
                    Xóa bộ lọc
                  </Button>
                )}
              </Space>
            </div>
            <div className={styles.actionButtons}>
              <Space size="middle">
                <Tooltip title="Làm mới dữ liệu">
                  <Button
                    icon={<i className="fas fa-sync-alt" />}
                    onClick={() => window.location.reload()}
                    className={styles.actionButton}
                  >
                    Làm mới
                  </Button>
                </Tooltip>
                <Tooltip title="Tạo phiếu nhập kho mới">
                  <Button
                    type="primary"
                    icon={<i className="fas fa-plus" />}
                    onClick={handleAdd}
                    className={styles.primaryButton}
                  >
                    Tạo phiếu nhập
                  </Button>
                </Tooltip>
              </Space>
            </div>
          </div>

          <Table
            columns={columns}
            dataSource={filteredRecords}
            rowKey="id"
            loading={isLoading}
            className={styles.stockInTable}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} của ${total} phiếu nhập kho`,
              className: styles.tablePagination,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => setPagination(prev => ({ ...prev, current: page, pageSize }))
            }}
            size="middle"
            scroll={{ x: 800 }}
          />
        </Card>

      {/* Stock In Print Modal */}
      <StockInPrintModal
        visible={printModalVisible}
        onCancel={handlePrintModalCancel}
        data={printDetailRecord}
        isLoading={isLoadingPrintDetail}
      />
      </div>
    </ConfigProvider>
  );
};

export default StockInPage; 